import { useAuth0 } from '@auth0/auth0-react';
import { Box, Chip, Typography } from '@mui/material';
import React from 'react';

import { ProxySession, ProxyTimer } from '../../ProxyTimer';

interface AdminProxyBannerProps {
  proxySession?: ProxySession | null;
  onEndProxySession?: () => Promise<void>;
  onRedirect?: () => void;
}

const AdminProxyBanner: React.FC<AdminProxyBannerProps> = ({
  proxySession,
  onEndProxySession,
  onRedirect,
}) => {
  const { user: auth0User } = useAuth0();

  // Check if user is an admin proxying (auth0_user_id starts with "okta|armhr-ops")
  const isAdminProxying = auth0User?.sub?.startsWith('okta|armhr-ops');

  if (!isAdminProxying) return null;

  const userMetadata = auth0User?.user_metadata;

  return (
    <Box
      sx={{
        // backgroundColor: '#fff7ed',
        // border: '1px solid #fed7aa',
        // red schema light opacity
        backgroundColor: '#D32F2F',
        color: 'white',
        borderTop: 'none',
        px: 2.5,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#ea580c',
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '0.85rem',
          }}
        >
          Admin Proxy
        </Typography>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {/* Current User Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                color: 'white',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                minWidth: 60,
              }}
            >
              Proxying as:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Metadata Chips */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {userMetadata?.prism_client_id && (
                  <Chip
                    label={`Client: ${userMetadata.prism_client_id}`}
                    size="small"
                    sx={{
                      height: 18,
                      backgroundColor: '#f0fdf4',
                      color: '#15803d',
                      border: '1px solid #bbf7d0',
                      '& .MuiChip-label': {
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        px: 0.5,
                        fontSize: '0.7rem',
                      },
                    }}
                  />
                )}
                {userMetadata?.prism_user_id && (
                  <Chip
                    label={`User: ${userMetadata.prism_user_id}`}
                    size="small"
                    sx={{
                      height: 18,
                      backgroundColor: '#fef3c7',
                      color: '#a16207',
                      border: '1px solid #fde68a',
                      '& .MuiChip-label': {
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        px: 0.5,
                        fontSize: '0.7rem',
                      },
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Proxy Timer - shown when proxy session data is available */}
      {proxySession && (
        <Box sx={{ mx: 2, flexShrink: 0, width: 180 }}>
          <ProxyTimer
            proxySession={proxySession}
            onEndSession={onEndProxySession}
            onRedirect={onRedirect}
            compact
          />
        </Box>
      )}

      {/* DEV Badge */}
      <Chip
        label="ADMIN ONLY"
        size="small"
        variant="outlined"
        sx={{ fontSize: '0.65rem', height: '20px', color: 'white' }}
      />
    </Box>
  );
};

export default AdminProxyBanner;
